/**
 * Export handlers for the foundations screens (G2.4, G2 test d).
 *
 * One per data route. Each returns the CURRENT view — G2.4's fidelity rule is that
 * an export reflects the filters, sort, column set and drill level the planner is
 * looking at, not the unfiltered underlying query — and each carries all nine
 * provenance fields, which the type makes non-optional (G2 test e).
 *
 * Numbers leave as `raw`. There is no path in `ExportCell` that carries a display
 * string for a number, so "full precision, unrounded, never a display string" is a
 * type property rather than a discipline.
 *
 * reqs/00 D8's three states leave as `{ kind: 'missing', state }`, not as zero. A
 * CSV that renders *not ranged* and *counted zero* both as `0` has destroyed the
 * distinction the whole chapter is built on.
 */

import { callOperation, collectPages } from '@/api';
import {
  foundationsListExceptions, listAutonomyRegister, listChanges, listExceptionRules, listFeeds, listHierarchies, listPlanVersions,
  listScenarios, readCalendar, readModules, registryListParameters,
  type AlertSummary, type AutonomySummary, type ChangeSetSummary, type ParameterKeyEntry, type ParameterRow, type PlanVersionSummary,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, PERIMETER_NOT_RECORDED, buildVersion } from '@/export';
import type { ExportCell, ExportRequest, Provenance } from '@/export';

import type { ExportScope } from '../manifest';

import { planIdsFromScenarios, toFeedState } from './api';
import { SEVERITY_LABEL_KEY } from './alert-groups-inbox';
import {
  ALERT_NAME_EXPORT_COLUMNS, ALERT_NAME_EXPORT_KEYS, PERSON_NAME_DISPLAY, alertNameExportCells, decidedByExportCell, peopleExportCell, personExportCell,
} from '@/planning';
import { STATE_LABEL_KEY } from './alert-queue';
import { categoryLabelKey } from './change-labels';
import { changeStateKey } from './change-record';
import { freshnessKey } from './data-schedules';
import { moduleStateKey, sourceKey } from './modules';
import { AUTONOMY_LEVEL_LABEL_KEY, exportDisplay, exportDisplayOf, FEED_LABEL_KEY, ROLE_LABEL_KEY } from '@/planning/words';

/** A key function's fallback is no words: the workbook shows the code instead (audit E2). */
const notFallback = (key: string, fallback: string): string | null => (key === fallback ? null : key);

/** A severity in the queue's words, only when it is exactly one of the four classes. */
const severityDisplay = exportDisplayOf(SEVERITY_LABEL_KEY);

/** A feed's certification state in the strip's words (`FEED_STATES`, `@/planning`). */
const FEED_STATE_KEY: Readonly<Record<string, string>> = {
  certified: 'feeds.state.certified',
  degraded: 'feeds.state.degraded',
  quarantined: 'feeds.state.quarantined',
  awaiting: 'feeds.state.awaiting',
};
import { parameterValue, sectionOf } from './parameters';
import { AS_OF_PARAM } from './scope';
import {
  alertListProvenance, alertQueueQuery, alertsOwnedBy, ownerName, readAlertDepartmentScope, readAlertQueueScope, readAlertSubjectScope,
} from './alert-queue';
import { alertGroupFilter, alertItemsQuery, readAlertGroupPageScope } from './alert-groups';
import { readCountry } from './data-schedules';
import { PERIOD_TYPE_KEY } from './process-calendar/calendar-items';
import { readYear } from './process-calendar/scope';
import { todayIso } from './proposal-defaults';

/** The build that produced the file — `buildVersion` in `@/export`, `docs/adr/9007`. */
const PLATFORM_VERSION = buildVersion();

const provenance = (
  measures: readonly { measure: string; definition: string }[],
): Provenance => ({
  // The screen does not record the perimeter it read yet; no filter list is claimed (ADR 9007).
  filters: PERIMETER_NOT_RECORDED,
  hierarchyNode: NOT_PUBLISHED,
  timeWindow: NOT_PUBLISHED,
  scenario: NOT_PUBLISHED,
  planVersionId: NOT_PUBLISHED,
  measureDefinitions: measures.map((entry) => ({
    measure: entry.measure,
    definition: entry.definition,
    registerVersion: '',
  })),
  generatedAt: new Date().toISOString(),
  generatedBy: NO_PRINCIPAL,
  platformVersion: PLATFORM_VERSION,
});

export async function planVersionExport(): Promise<ExportRequest> {
  // G2.4's fidelity rule is that an export reflects what the planner is looking at, so
  // the rows come from the same operations the screen reads and not from a second source.
  const scenarios = await collectPages(listScenarios, { query: {} });
  const planId = planIdsFromScenarios(scenarios)[0];
  const versions = planId === undefined
    ? []
    : await collectPages(listPlanVersions, { path: { plan_id: planId } });
  return planVersionRequest(versions);
}

/** The version history's sheet over versions already read (`docs/adr/9008`). */
export function planVersionRequest(versions: readonly PlanVersionSummary[]): ExportRequest {
  const rows: ExportCell[][] = versions.map((version) => [
    { kind: 'text', value: version.versionId },
    { kind: 'text', value: version.parentVersionId ?? '' },
    { kind: 'label', value: version.kind, code: version.kind },
    { kind: 'label', value: version.status, code: version.status },
    personExportCell(version.author, version.authorName, version.authorNameAbsentReason),
    { kind: 'text', value: version.author },
    { kind: 'date', iso: version.recordedAt },
    // `PlanVersionSummary` publishes no cell count. `not-reported`, never a zero: a CSV
    // rendering an unmeasured count as 0 destroys the distinction reqs/00 D8 is built on,
    // and a spreadsheet will happily sum it.
    { kind: 'missing', state: 'not-reported' },
  ]);
  return {
    id: 'foundations.versions.table',
    titleKey: 'foundations.versions.title',
    columns: [
      { key: 'version_id', headerKey: 'foundations.column.versionId', kind: 'text', descriptionKey: 'foundations.columnGuide.version.versionId' },
      { key: 'parent_version_id', headerKey: 'foundations.column.parentVersionId', kind: 'text', descriptionKey: 'foundations.columnGuide.version.parentVersionId' },
      { key: 'kind', headerKey: 'foundations.column.kind', kind: 'label', descriptionKey: 'foundations.columnGuide.version.kind' },
      { key: 'status', headerKey: 'foundations.column.status', kind: 'label', descriptionKey: 'foundations.columnGuide.version.status' },
      { key: 'author_name', headerKey: 'foundations.column.author', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'foundations.columnGuide.people.planAuthorName' },
      { key: 'author', headerKey: 'foundations.people.column.authorId', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.authorId' },
      { key: 'recorded_at', headerKey: 'foundations.column.recordedAt', kind: 'date', descriptionKey: 'foundations.columnGuide.version.recordedAt' },
      { key: 'cells', headerKey: 'foundations.column.cells', kind: 'integer', unit: 'cells', descriptionKey: 'foundations.columnGuide.version.cells' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'plan_version', definition: 'An immutable version of a plan (reqs/00 F3).' },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'recorded_at', direction: 'desc' }],
      visibleColumns: ['version_id', 'parent_version_id', 'kind', 'status', 'author_name', 'author', 'recorded_at', 'cells'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

export async function hierarchyExport(): Promise<ExportRequest> {
  const schemes = await collectPages(listHierarchies, { query: {} });
  const rows: ExportCell[][] = schemes.map((scheme) => [
    { kind: 'text', value: scheme.schemeId },
    { kind: 'text', value: scheme.versionId },
    { kind: 'text', value: scheme.dimension },
    { kind: 'text', value: scheme.owner },
    { kind: 'label', value: String(scheme.isActive), code: String(scheme.isActive) },
  ]);
  return {
    id: 'foundations.hierarchies.table',
    titleKey: 'foundations.hierarchies.title',
    columns: [
      { key: 'scheme_id', headerKey: 'foundations.column.schemeId', kind: 'text', descriptionKey: 'foundations.columnGuide.hierarchy.schemeId' },
      { key: 'version_id', headerKey: 'foundations.column.versionId', kind: 'text', descriptionKey: 'foundations.columnGuide.hierarchy.versionId' },
      { key: 'dimension', headerKey: 'foundations.column.dimension', kind: 'text', descriptionKey: 'foundations.columnGuide.hierarchy.dimension' },
      { key: 'owner', headerKey: 'foundations.column.owner', kind: 'text', descriptionKey: 'foundations.columnGuide.hierarchy.owner' },
      { key: 'is_active', headerKey: 'foundations.column.active', kind: 'label', descriptionKey: 'foundations.columnGuide.hierarchy.isActive' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'hierarchy_scheme', definition: 'A versioned, coexisting hierarchy scheme (reqs/00 D4).' },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'scheme_id', direction: 'asc' }],
      visibleColumns: ['scheme_id', 'version_id', 'dimension', 'owner', 'is_active'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The scenario list the comparison screen switches between — `GET /foundations/scenarios`,
 * the screen's own read. The bridge this route used to export was a fixture and was removed
 * with its panel on 2026-09-27 (F8).
 */
export async function scenarioListExport(): Promise<ExportRequest> {
  const scenarios = await collectPages(listScenarios, { query: {} });
  const rows: ExportCell[][] = scenarios.map((scenario) => [
    { kind: 'text', value: scenario.scenarioId },
    { kind: 'text', value: scenario.planId },
    { kind: 'text', value: scenario.baseVersionId },
    // Counted by the list statement since bata-platform DEC-111: the scenario's own overlay cells.
    { kind: 'integer', raw: scenario.overlayCells },
  ]);
  return {
    id: 'foundations.scenarios.table',
    titleKey: 'foundations.scenarios.title',
    columns: [
      { key: 'scenario_id', headerKey: 'foundations.column.scenarioId', kind: 'text', descriptionKey: 'foundations.columnGuide.scenario.scenarioId' },
      { key: 'plan_id', headerKey: 'foundations.column.planId', kind: 'text', descriptionKey: 'foundations.columnGuide.scenario.planId' },
      { key: 'base_version_id', headerKey: 'foundations.column.baseVersionId', kind: 'text', descriptionKey: 'foundations.columnGuide.scenario.baseVersionId' },
      { key: 'overlay_cells', headerKey: 'foundations.scenarios.overlayCells', kind: 'integer', unit: 'cells', descriptionKey: 'foundations.columnGuide.scenario.overlayCells' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'scenario', definition: 'A scenario branched from an approved plan version (reqs/00 F4).' },
    ]),
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: ['scenario_id', 'plan_id', 'base_version_id', 'overlay_cells'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The rule catalogue with perimeters (bata-ui#166 W3-5, ADR 2103). */
export async function exceptionRulesExport(): Promise<ExportRequest> {
  const rules = await callOperation(listExceptionRules, {});
  const rows: ExportCell[][] = rules.map((rule) => [
    { kind: 'text', value: rule.ruleId },
    { kind: 'text', value: rule.measure },
    { kind: 'text', value: rule.comparator },
    { kind: 'label', value: String(rule.isActive), code: String(rule.isActive) },
    { kind: 'label', value: rule.severity, code: rule.severity },
    { kind: 'number', raw: rule.slaHours },
    { kind: 'text', value: rule.owner },
    // No perimeter: the rule covers every observation it is active for (ADR 2101).
    rule.perimeter.length === 0
      ? { kind: 'label', value: 'no_perimeter', code: 'no_perimeter' }
      : { kind: 'text', value: rule.perimeter.map((pair) => [pair.dimension, pair.value].join('=')).join(', ') },
    { kind: 'text', value: rule.thresholdParameterKey },
  ]);
  return {
    id: 'foundations.rules.table',
    titleKey: 'foundations.rules.title',
    columns: [
      { key: 'rule_id', headerKey: 'foundations.column.ruleId', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.ruleId' },
      { key: 'measure', headerKey: 'foundations.column.measure', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.measure' },
      { key: 'comparator', headerKey: 'foundations.rules.column.comparator', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.comparator' },
      { key: 'is_active', headerKey: 'foundations.rules.column.active', kind: 'label', descriptionKey: 'foundations.columnGuide.rule.isActive' },
      { key: 'severity', headerKey: 'foundations.column.severity', kind: 'label', display: severityDisplay, descriptionKey: 'foundations.columnGuide.rule.severity' },
      { key: 'sla_hours', headerKey: 'foundations.rules.column.sla', kind: 'number', descriptionKey: 'foundations.columnGuide.rule.slaHours' },
      { key: 'owner', headerKey: 'foundations.rules.column.owner', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.owner' },
      { key: 'perimeter', headerKey: 'foundations.rules.column.perimeter', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.perimeter' },
      { key: 'threshold_parameter_key', headerKey: 'foundations.rules.column.thresholdKey', kind: 'text', descriptionKey: 'foundations.columnGuide.rule.thresholdKey' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([]),
    perimeter: {
      filters: [],
      sort: [{ key: 'rule_id', direction: 'asc' }],
      visibleColumns: ['rule_id', 'measure', 'comparator', 'is_active', 'severity', 'sla_hours', 'owner', 'perimeter', 'threshold_parameter_key'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

export async function exceptionQueueExport(scope?: ExportScope): Promise<ExportRequest> {
  // The queue the link names (`?queue=&state=&overdue=`, W3-4, and `?country=&department=`,
  // ADR 2105), so the file is the view.
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  // The subject filters too (usability audit C5): rule, store and article sent to the read, the
  // owner matched over its rows, exactly as the list does.
  const scoped = readAlertQueueScope(search);
  const department = readAlertDepartmentScope(search);
  const subject = readAlertSubjectScope(search);
  // Most severe first, as the list draws them (ADR 2129).
  const alerts = await collectPages(foundationsListExceptions, {
    query: { ...alertQueueQuery(scoped, department, subject), order: 'severity_oldest' },
  });
  return alertListRequest(alertsOwnedBy(alerts, subject.owner), alertListProvenance(scoped, department, subject));
}

/** The single alerts' file over rows already read — the list's view, with the filters that made it. */
export function alertListRequest(
  alerts: readonly AlertSummary[], filters: readonly { readonly field: string; readonly operator: string; readonly value: string }[],
): ExportRequest {
  return alertRowsRequest(alerts, 'foundations.exceptions.table', 'foundations.exceptions.title', filters);
}

/**
 * The group page's export (DEC-101, ADR 9040): every alert of the group's filter as the link names
 * it — the rule, the dimension's value, the narrowing, the queue, state and overdue — the same set
 * an act on the page takes. The table's own CSV/Excel carries the planner's ranking and filters.
 */
export async function alertGroupExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const group = readAlertGroupPageScope(search);
  if (group === null) return alertRowsRequest([], 'foundations.alertGroup.table', 'foundations.alertGroups.itemsExportTitle', []);
  const filter = alertGroupFilter(group, true);
  const alerts = await collectPages(foundationsListExceptions, { query: alertItemsQuery(filter) });
  const equals = (field: string, value: string | boolean | undefined) => (value === undefined
    ? []
    : [{ field, operator: 'equals', value: String(value) }]);
  return alertRowsRequest(alerts, 'foundations.alertGroup.table', 'foundations.alertGroups.itemsExportTitle', [
    ...equals('rule_id', filter.ruleId),
    ...equals('country_key', filter.countryKey),
    ...equals('store_key', filter.storeKey),
    ...equals('department_family', filter.departmentFamily),
    ...equals('queue', filter.queue),
    ...equals('state', filter.state),
    ...equals('overdue', filter.overdue),
  ]);
}

function alertRowsRequest(
  alerts: readonly AlertSummary[], id: string, titleKey: string,
  filters: readonly { readonly field: string; readonly operator: string; readonly value: string }[],
): ExportRequest {
  const optional = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'text', value });
  const rows: ExportCell[][] = alerts.map((alert) => [
    { kind: 'text', value: alert.alertId },
    // The rule id leaves in full. The queue can only show a catalogue label for a rule it
    // names, so the export is where an unclassified rule stays identifiable.
    { kind: 'text', value: alert.ruleId },
    { kind: 'label', value: alert.severity, code: alert.severity },
    { kind: 'text', value: alert.subjectId },
    // What the alert is about, by name beside the code (usability audit B4): the store and the
    // article as the alert names them, each name's absent reason technical.
    ...alertNameExportCells(alert),
    { kind: 'text', value: alert.measure },
    { kind: 'number', raw: alert.observed },
    { kind: 'number', raw: alert.threshold },
    { kind: 'date', iso: alert.raisedAt },
    alert.dueAt === null || alert.dueAt === undefined ? { kind: 'missing', state: 'not-reported' } : { kind: 'date', iso: alert.dueAt },
    { kind: 'label', value: alert.state ?? 'open', code: alert.state ?? 'open' },
    // The owner as the queue shows it (usability audit B5, DEC-102): their profile name, else
    // their id; nobody is not reported. The id itself stays beside it, technical.
    optional(ownerName(alert)?.text),
    optional(alert.assignee),
    optional(alert.snoozedUntil),
    // Who decided the alert (bata-platform ADR 9021): blank while it is undecided.
    decidedByExportCell({
      disposition: alert.disposition ?? null, actor: alert.dispositionActor ?? null, actorName: alert.dispositionActorName ?? null,
      actorNameAbsentReason: alert.dispositionActorNameAbsentReason ?? null,
    }),
  ]);
  return {
    id,
    titleKey,
    columns: [
      { key: 'alert_id', headerKey: 'foundations.column.alertId', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.alertId' },
      { key: 'rule_id', headerKey: 'foundations.column.ruleId', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.ruleId' },
      { key: 'severity', headerKey: 'foundations.column.severity', kind: 'label', display: severityDisplay, descriptionKey: 'foundations.columnGuide.alert.severity' },
      { key: 'subject', headerKey: 'foundations.column.subject', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.subject' },
      ...ALERT_NAME_EXPORT_COLUMNS,
      { key: 'measure', headerKey: 'foundations.column.measure', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.measure' },
      { key: 'observed', headerKey: 'foundations.column.observed', kind: 'number', descriptionKey: 'foundations.columnGuide.alert.observed' },
      { key: 'threshold', headerKey: 'foundations.column.threshold', kind: 'number', descriptionKey: 'foundations.columnGuide.alert.threshold' },
      { key: 'raised_at', headerKey: 'foundations.column.raisedAt', kind: 'date', descriptionKey: 'foundations.columnGuide.alert.raisedAt' },
      { key: 'due_at', headerKey: 'foundations.alerts.column.dueAt', kind: 'date', descriptionKey: 'foundations.columnGuide.alert.dueAt' },
      { key: 'state', headerKey: 'foundations.alerts.column.state', kind: 'label', display: exportDisplayOf(STATE_LABEL_KEY), descriptionKey: 'foundations.columnGuide.alert.state' },
      { key: 'owner', headerKey: 'foundations.alerts.column.owner', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.owner' },
      { key: 'assignee', headerKey: 'foundations.alerts.column.ownerId', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.alert.assignee' },
      { key: 'snoozed_until', headerKey: 'foundations.alerts.column.snoozedUntil', kind: 'text', descriptionKey: 'foundations.columnGuide.alert.snoozedUntil' },
      {
        key: 'decided_by', headerKey: 'person.decidedBy.column', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'person.decidedBy.help',
        absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'person.decidedBy.absent.pending' } },
      },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'alert_dismissal_rate', definition: 'Share of disposed alerts for a rule that were dismissed.' },
    ]),
    perimeter: {
      // The set the link named (W3-4; DEC-101): which queue, which state, past-due only when asked.
      filters,
      sort: [{ key: 'raised_at', direction: 'asc' }],
      visibleColumns: [
        'alert_id', 'rule_id', 'severity', 'subject', ...ALERT_NAME_EXPORT_KEYS, 'measure', 'observed', 'threshold', 'raised_at',
        'due_at', 'state', 'owner', 'assignee', 'snoozed_until', 'decided_by',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The route's export: every feed row of the market the link names (`?country=`, ADR 2006), or
 * of every market in the caller's scope without one — the batch in force, its as-of date, the
 * age and the contract's own verdict on it, and whether an accumulating feed is double counted.
 */
export async function feedStatusExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const country = readCountry(search);
  const feeds = await collectPages(listFeeds, { query: country === null ? {} : { country_key: country } });
  const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'text', value });
  const rows: ExportCell[][] = feeds.map((feed) => {
    const state = toFeedState(feed);
    return [
      { kind: 'label', value: feed.feedId, code: feed.feedId },
      text(feed.countryKey),
      { kind: 'text', value: feed.stage },
      { kind: 'label', value: state, code: state },
      typeof feed.freshnessHours === 'number'
        ? { kind: 'number', raw: feed.freshnessHours }
        : { kind: 'missing', state: 'not-reported' },
      feed.freshnessState === undefined
        ? { kind: 'missing', state: 'not-reported' }
        : { kind: 'label', value: feed.freshnessState, code: feed.freshnessState },
      typeof feed.contract?.freshnessErrorAfterHours === 'number'
        ? { kind: 'number', raw: feed.contract.freshnessErrorAfterHours }
        : { kind: 'missing', state: 'not-reported' },
      typeof feed.varianceRate === 'number'
        ? { kind: 'number', raw: feed.varianceRate }
        : { kind: 'missing', state: 'not-reported' },
      text(feed.inForceBatchId ?? feed.lastBatchId),
      text(feed.windowEnd),
      typeof feed.unsupersededBatches === 'number'
        ? { kind: 'number', raw: feed.unsupersededBatches }
        : { kind: 'missing', state: 'not-reported' },
      { kind: 'label', value: String(feed.doubleCounted === true), code: String(feed.doubleCounted === true) },
    ];
  });
  return {
    id: 'foundations.feeds.table',
    titleKey: 'foundations.feeds.title',
    columns: [
      // The feed by its name in the workbook (audit A5/E2); the CSV keeps the id.
      { key: 'feed_id', headerKey: 'foundations.column.feedId', kind: 'label', display: exportDisplayOf(FEED_LABEL_KEY), descriptionKey: 'foundations.columnGuide.feed.feedId' },
      { key: 'country_key', headerKey: 'foundations.column.country', kind: 'text', descriptionKey: 'foundations.columnGuide.feed.countryKey' },
      { key: 'stage', headerKey: 'foundations.column.stage', kind: 'text', descriptionKey: 'foundations.columnGuide.feed.stage' },
      { key: 'state', headerKey: 'foundations.column.state', kind: 'label', display: exportDisplayOf(FEED_STATE_KEY), descriptionKey: 'foundations.columnGuide.feed.state' },
      { key: 'freshness_hours', headerKey: 'foundations.column.freshnessHours', kind: 'number', descriptionKey: 'foundations.columnGuide.feed.freshnessHours' },
      { key: 'freshness_state', headerKey: 'foundations.column.freshnessVerdict', kind: 'label', display: exportDisplay((code) => notFallback(freshnessKey(code), 'foundations.feeds.freshness.other')), descriptionKey: 'foundations.columnGuide.feed.freshnessState' },
      { key: 'freshness_error_after_hours', headerKey: 'foundations.column.freshnessThreshold', kind: 'number', descriptionKey: 'foundations.columnGuide.feed.freshnessThreshold' },
      { key: 'variance_rate', headerKey: 'foundations.column.varianceRate', kind: 'number', descriptionKey: 'foundations.columnGuide.feed.varianceRate' },
      { key: 'in_force_batch_id', headerKey: 'foundations.column.batchInForce', kind: 'text', descriptionKey: 'foundations.columnGuide.feed.inForceBatch' },
      { key: 'window_end', headerKey: 'foundations.column.asOf', kind: 'text', descriptionKey: 'foundations.columnGuide.feed.windowEnd' },
      { key: 'unsuperseded_batches', headerKey: 'foundations.column.unsupersededBatches', kind: 'number', descriptionKey: 'foundations.columnGuide.feed.unsupersededBatches' },
      { key: 'double_counted', headerKey: 'foundations.column.doubleCounted', kind: 'label', descriptionKey: 'foundations.columnGuide.feed.doubleCounted' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'feed_freshness_hours', definition: 'Hours from the batch in force\u2019s window end (00:00Z) to the read\u2019s instant (bata-platform ADR 1003).' },
      { measure: 'feed_freshness_state', definition: 'fresh or stale against the freshness_error_after_hours the feed\u2019s own contract declares; undeclared when the contract declares none; no_batch when the market never landed the feed.' },
      { measure: 'feed_double_counted', definition: 'An accumulating feed whose in-force partition holds more than one promoted, unsuperseded batch: the platform\u2019s reader sums them all (W0-3b).' },
    ]),
    perimeter: {
      filters: country === null ? [] : [{ field: 'country_key', operator: '=', value: country }],
      sort: [{ key: 'feed_id', direction: 'asc' }],
      visibleColumns: [
        'feed_id', 'country_key', 'stage', 'state', 'freshness_hours', 'freshness_state', 'freshness_error_after_hours',
        'variance_rate', 'in_force_batch_id', 'window_end', 'unsuperseded_batches', 'double_counted',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The parameter register, as at the date the link names (G2.4's fidelity rule).
 *
 * `as_of` is taken from the scope the export was requested from and is NEVER defaulted to
 * today — the same rule the screen follows and the one the contract states. Without it the
 * sheet is EMPTY rather than "the whole register at whatever moment the export ran",
 * because a registry answered for an unstated date is the unbounded read `as_of` exists to
 * close (G16), and a spreadsheet carrying it would outlive every clue about which date it
 * was taken at.
 *
 * The recorded baseline is deliberately NOT a column here. It costs a second read per
 * change set, and a sheet that issued them would describe a different perimeter from the
 * screen the moment one of those pages was truncated. What leaves is what the register
 * itself says in one read.
 */
export async function parameterRegistryExport(given?: ExportScope): Promise<ExportRequest> {
  const asOf = given?.params[AS_OF_PARAM]?.trim() ?? '';
  return parameterRegistryRequest(
    asOf === '' ? [] : await collectPages(registryListParameters, { query: { as_of: asOf } }),
    asOf,
  );
}

/**
 * The register's sheet over rows already read at `asOf` — the route's export above, and the
 * CSV under one capped section table (`docs/adr/9008`), which hands in that section's rows.
 */
export function parameterRegistryRequest(
  parameters: readonly ParameterRow[], asOf: string,
): ExportRequest {
  const rows: ExportCell[][] = parameters.map((row) => {
      const value = parameterValue(row);
      return [
        { kind: 'text', value: sectionOf(row.parameterKey) ?? '' },
        { kind: 'text', value: row.parameterKey },
        { kind: 'text', value: row.scopeDimension },
        { kind: 'text', value: row.scopeValue },
        // The two value columns stay two columns. Collapsing a threshold and a model
        // version into one cell is exactly what `ParameterRow` refuses to do, and a
        // spreadsheet would coerce whichever survived on open.
        value.kind === 'numeric'
          ? { kind: 'number', raw: value.value }
          : { kind: 'missing', state: 'not-reported' },
        value.kind === 'text'
          ? { kind: 'text', value: value.value }
          : { kind: 'missing', state: 'not-reported' },
        { kind: 'text', value: row.owner },
        { kind: 'date', iso: row.reviewDate },
        { kind: 'date', iso: row.validFrom },
        { kind: 'date', iso: row.validTo },
        { kind: 'text', value: row.changeId },
      ] satisfies ExportCell[];
    });

  return {
    id: 'foundations.parameters.table',
    titleKey: 'foundations.parameters.title',
    columns: [
      { key: 'section', headerKey: 'foundations.column.section', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.section' },
      { key: 'parameter_key', headerKey: 'foundations.column.parameterKey', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.key' },
      { key: 'scope_dimension', headerKey: 'foundations.column.scopeDimension', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.scopeDimension' },
      { key: 'scope_value', headerKey: 'foundations.column.scopeValue', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.scopeValue' },
      { key: 'numeric_value', headerKey: 'foundations.column.numericValue', kind: 'number', descriptionKey: 'foundations.columnGuide.parameter.numericValue' },
      { key: 'text_value', headerKey: 'foundations.column.textValue', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.textValue' },
      { key: 'owner', headerKey: 'foundations.column.owner', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.owner' },
      { key: 'review_date', headerKey: 'foundations.column.reviewDate', kind: 'date', descriptionKey: 'foundations.columnGuide.parameter.reviewDate' },
      { key: 'valid_from', headerKey: 'foundations.column.validFrom', kind: 'date', descriptionKey: 'foundations.columnGuide.parameter.validFrom' },
      { key: 'valid_to', headerKey: 'foundations.column.validTo', kind: 'date', descriptionKey: 'foundations.columnGuide.parameter.validTo' },
      { key: 'change_id', headerKey: 'foundations.column.changeId', kind: 'text', descriptionKey: 'foundations.columnGuide.parameter.changeId' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'plan_parameter',
        definition:
          'One row of the effective-dated parameter registry (G1): a value in force over a '
          + 'half-open validity window, with an owner and a review date.',
      },
    ]),
    perimeter: {
      filters: asOf === '' ? [] : [{ field: 'as_of', operator: 'equals', value: asOf }],
      sort: [{ key: 'parameter_key', direction: 'asc' }],
      visibleColumns: [
        'section', 'parameter_key', 'scope_dimension', 'scope_value', 'numeric_value',
        'text_value', 'owner', 'review_date', 'valid_from', 'valid_to', 'change_id',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The key catalogue as one section table draws it (`docs/adr/2004`) — every key the code
 * reads, including the unset ones, with the dimensions its reader honours. An absent code
 * default leaves as a missing cell, never as zero.
 */
export function parameterCatalogueRequest(
  entries: readonly ParameterKeyEntry[], asOf: string,
): ExportRequest {
  const rows: ExportCell[][] = entries.map((entry) => [
    { kind: 'text', value: sectionOf(entry.parameterKey) ?? '' },
    { kind: 'text', value: entry.parameterKey },
    { kind: 'text', value: entry.status },
    { kind: 'text', value: entry.valueType },
    { kind: 'text', value: entry.honouredDimensions.join(' ') },
    { kind: 'text', value: entry.requiredDimensions.join(' ') },
    { kind: 'text', value: entry.estateHonoured ? 'true' : 'false' },
    entry.defaultNumeric === null || entry.defaultNumeric === undefined
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'number', raw: entry.defaultNumeric },
    entry.defaultText === null || entry.defaultText === undefined
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'text', value: entry.defaultText },
    { kind: 'number', raw: entry.rowsInForce },
    { kind: 'text', value: entry.ownerChapter },
  ] satisfies ExportCell[]);

  return {
    id: 'foundations.parameters.catalogue',
    titleKey: 'foundations.catalogue.title',
    columns: [
      { key: 'section', headerKey: 'foundations.column.section', kind: 'text' },
      { key: 'parameter_key', headerKey: 'foundations.column.parameterKey', kind: 'text' },
      { key: 'status', headerKey: 'foundations.catalogue.column.status', kind: 'text' },
      { key: 'value_type', headerKey: 'foundations.catalogue.column.valueType', kind: 'text' },
      { key: 'honoured_dimensions', headerKey: 'foundations.catalogue.column.honoured', kind: 'text' },
      { key: 'required_dimensions', headerKey: 'foundations.catalogue.column.required', kind: 'text' },
      { key: 'estate_honoured', headerKey: 'foundations.catalogue.column.estateHonoured', kind: 'text' },
      { key: 'default_numeric', headerKey: 'foundations.catalogue.column.defaultNumeric', kind: 'number' },
      { key: 'default_text', headerKey: 'foundations.catalogue.column.defaultText', kind: 'text' },
      { key: 'rows_in_force', headerKey: 'foundations.catalogue.column.rowsInForce', kind: 'number' },
      { key: 'owner_chapter', headerKey: 'foundations.catalogue.column.ownerChapter', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'parameter_key_catalogue',
        definition:
          'One key the platform code reads (bata-platform ADR 9007): its value type, the scope '
          + 'dimensions its reader honours, its owning chapter and whether any row is in force.',
      },
    ]),
    perimeter: {
      filters: asOf === '' ? [] : [{ field: 'as_of', operator: 'equals', value: asOf }],
      sort: [{ key: 'parameter_key', direction: 'asc' }],
      visibleColumns: [
        'section', 'parameter_key', 'status', 'value_type', 'honoured_dimensions',
        'required_dimensions', 'estate_honoured', 'default_numeric', 'default_text',
        'rows_in_force', 'owner_chapter',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The change sets awaiting approval, as the pending-approvals table draws them — every one
 * the category filter admits, of which the table draws fifty (`docs/adr/9008`).
 *
 * The filter the planner applied is a filter row; the state is always `proposed`, because
 * that is the only state the read asks for.
 */
export function pendingApprovalsRequest(
  changes: readonly ChangeSetSummary[], category: string | null,
  /** The register filters the queue was read under (usability audit C8), as provenance triples. */
  narrowing: readonly { readonly field: string; readonly operator: string; readonly value: string }[] = [],
): ExportRequest {
  const date = (value: string | null | undefined): ExportCell => (
    value === null || value === undefined || value === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'date', iso: value }
  );
  const rows: ExportCell[][] = changes.map((change) => [
    { kind: 'text', value: change.changeId },
    { kind: 'label', value: change.category, code: change.category },
    // By name (usability audit B6), the id beside it, technical.
    personExportCell(change.author, change.authorName, change.authorNameAbsentReason),
    { kind: 'text', value: change.author },
    date(change.proposedOn),
    date(change.reviewDueOn),
    { kind: 'label', value: change.state, code: change.state },
  ]);
  const filters = [
    { field: 'state', operator: 'equals', value: 'proposed' },
    ...(category === null ? [] : [{ field: 'category', operator: 'equals', value: category }]),
    ...narrowing.filter((filter) => filter.field !== 'state'),
  ];
  return {
    id: 'foundations.parameters.approvals',
    titleKey: 'foundations.approvals.title',
    columns: [
      { key: 'change_id', headerKey: 'foundations.approvals.column.changeId', kind: 'text', descriptionKey: 'foundations.columnGuide.change.changeId' },
      { key: 'category', headerKey: 'foundations.approvals.column.category', kind: 'label', display: exportDisplay((code) => notFallback(categoryLabelKey(code), 'foundations.changeCategory.unknown')), descriptionKey: 'foundations.columnGuide.change.category' },
      { key: 'author_name', headerKey: 'foundations.approvals.column.author', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'foundations.columnGuide.people.authorName' },
      { key: 'author', headerKey: 'foundations.people.column.authorId', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.authorId' },
      { key: 'proposed_on', headerKey: 'foundations.approvals.column.proposedOn', kind: 'date', descriptionKey: 'foundations.columnGuide.change.proposedOn' },
      { key: 'review_due_on', headerKey: 'foundations.approvals.column.reviewDueOn', kind: 'date', descriptionKey: 'foundations.columnGuide.change.reviewDueOn' },
      { key: 'state', headerKey: 'foundations.approvals.column.state', kind: 'label', display: exportDisplay((code) => notFallback(changeStateKey(code), 'foundations.approvals.state.unknown')), descriptionKey: 'foundations.columnGuide.change.stateProposed' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      ...provenance([
        {
          measure: 'parameter_change_set',
          definition:
            'A proposed change to the effective-dated parameter registry, awaiting approval. '
            + 'Nothing in it is in force until it is approved.',
        },
      ]),
      filters,
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ['change_id', 'category', 'author_name', 'author', 'proposed_on', 'review_due_on', 'state'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The approved change sets the promotion panel lists (`./promotions.tsx`, `docs/adr/2003`),
 * over the rows already read. The state is always `approved`: that is the only state the
 * read asks for.
 */
export function approvedChangesRequest(changes: readonly ChangeSetSummary[]): ExportRequest {
  const date = (value: string | null | undefined): ExportCell => (
    value === null || value === undefined || value === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'date', iso: value }
  );
  const rows: ExportCell[][] = changes.map((change) => [
    { kind: 'text', value: change.changeId },
    { kind: 'label', value: change.category, code: change.category },
    // By name (usability audit B6), the id beside it, technical.
    personExportCell(change.author, change.authorName, change.authorNameAbsentReason),
    { kind: 'text', value: change.author },
    date(change.approvedOn),
    peopleExportCell(change.approvedBy, change.approvedByNames),
    { kind: 'text', value: (change.approvedBy ?? []).join(';') },
    { kind: 'text', value: (change.tierPath ?? []).join(';') },
    { kind: 'label', value: change.state, code: change.state },
  ]);
  const filters = [{ field: 'state', operator: 'equals', value: 'approved' }];
  return {
    id: 'foundations.parameters.promotions',
    titleKey: 'foundations.promotions.title',
    columns: [
      { key: 'change_id', headerKey: 'foundations.approvals.column.changeId', kind: 'text' },
      { key: 'category', headerKey: 'foundations.approvals.column.category', kind: 'label', display: exportDisplay((code) => notFallback(categoryLabelKey(code), 'foundations.changeCategory.unknown')) },
      { key: 'author_name', headerKey: 'foundations.approvals.column.author', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'foundations.columnGuide.people.authorName' },
      { key: 'author', headerKey: 'foundations.people.column.authorId', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.authorId' },
      { key: 'approved_on', headerKey: 'foundations.promotions.column.approvedOn', kind: 'date' },
      { key: 'approved_by_names', headerKey: 'foundations.promotions.column.approvedBy', kind: 'text', descriptionKey: 'foundations.columnGuide.people.approvedByNames' },
      { key: 'approved_by', headerKey: 'foundations.people.column.approvedByIds', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.approvedByIds' },
      { key: 'tier_path', headerKey: 'foundations.promotions.column.tierPath', kind: 'text' },
      { key: 'state', headerKey: 'foundations.approvals.column.state', kind: 'label', display: exportDisplay((code) => notFallback(changeStateKey(code), 'foundations.approvals.state.unknown')) },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      ...provenance([
        {
          measure: 'parameter_change_set',
          definition:
            'An approved, sealed change to the effective-dated parameter registry. Approval '
            + 'applies it; the tier path records the tiers it has been promoted into.',
        },
      ]),
      filters,
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ['change_id', 'category', 'author_name', 'author', 'approved_on', 'approved_by_names', 'approved_by', 'tier_path', 'state'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * «Approvazioni in attesa» of the parameters (DEC-89): every change set awaiting approval, as
 * the page's own table exports them (`pendingApprovalsRequest`). The read is by state, never by
 * a date, so the scope adds nothing to it.
 */
export async function parameterApprovalsExport(): Promise<ExportRequest> {
  return pendingApprovalsRequest(await collectPages(listChanges, { query: { state: 'proposed' } }), null);
}

/**
 * «Scadenze e storico» (DEC-89): the change record — every change set, closed ones included,
 * with who proposed it, its state and its dates. The expiry table beside it is a warning over
 * the rows in force, which the register's own sheet already carries with their windows.
 */
export async function parameterHistoryExport(): Promise<ExportRequest> {
  return changeRecordRequest(await collectPages(listChanges, { query: {} }));
}

/** The change record's sheet over change sets already read. */
export function changeRecordRequest(changes: readonly ChangeSetSummary[]): ExportRequest {
  const date = (value: string | null | undefined): ExportCell => (
    value === null || value === undefined || value === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'date', iso: value }
  );
  const rows: ExportCell[][] = changes.map((change) => [
    { kind: 'text', value: change.changeId },
    { kind: 'label', value: change.category, code: change.category },
    // By name (usability audit B6), the id beside it, technical.
    personExportCell(change.author, change.authorName, change.authorNameAbsentReason),
    { kind: 'text', value: change.author },
    date(change.proposedOn),
    date(change.approvedOn),
    { kind: 'label', value: change.state, code: change.state },
  ]);
  return {
    id: 'foundations.parameters.history',
    titleKey: 'foundations.changeRecord.title',
    columns: [
      { key: 'change_id', headerKey: 'foundations.approvals.column.changeId', kind: 'text', descriptionKey: 'foundations.columnGuide.change.changeId' },
      { key: 'category', headerKey: 'foundations.approvals.column.category', kind: 'label', display: exportDisplay((code) => notFallback(categoryLabelKey(code), 'foundations.changeCategory.unknown')), descriptionKey: 'foundations.columnGuide.change.category' },
      { key: 'author_name', headerKey: 'foundations.approvals.column.author', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'foundations.columnGuide.people.authorName' },
      { key: 'author', headerKey: 'foundations.people.column.authorId', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.authorId' },
      { key: 'proposed_on', headerKey: 'foundations.approvals.column.proposedOn', kind: 'date', descriptionKey: 'foundations.columnGuide.change.proposedOn' },
      { key: 'approved_on', headerKey: 'foundations.promotions.column.approvedOn', kind: 'date', descriptionKey: 'foundations.columnGuide.change.approvedOn' },
      { key: 'state', headerKey: 'foundations.approvals.column.state', kind: 'label', display: exportDisplay((code) => notFallback(changeStateKey(code), 'foundations.approvals.state.unknown')), descriptionKey: 'foundations.columnGuide.change.state' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'parameter_change_set',
        definition:
          'A change to the effective-dated parameter registry, in any state: proposed, approved, '
          + 'promoted, rejected or withdrawn. Only an approved set is in force.',
      },
    ]),
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: ['change_id', 'category', 'author_name', 'author', 'proposed_on', 'approved_on', 'state'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The code an estate-wide autonomy row leaves the file with, in the country column. */
export const ESTATE_WIDE = 'estate_wide';

/** The autonomy register route's export: every row the read publishes (`docs/adr/2003`). */
export async function autonomyRegisterExport(): Promise<ExportRequest> {
  return autonomyRegisterRequest(await collectPages(listAutonomyRegister, { query: {} }));
}

/**
 * The autonomy register's sheet. A null ceiling leaves as `not-reported`, never as zero:
 * a zero value ceiling would read as "no autonomy at all", which the register did not say.
 */
export function autonomyRegisterRequest(rows: readonly AutonomySummary[]): ExportRequest {
  const optionalNumber = (value: number | null | undefined): ExportCell => (
    value === null || value === undefined ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value }
  );
  const optionalDate = (value: string | null | undefined): ExportCell => (
    value === null || value === undefined || value === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'date', iso: value }
  );
  const cells: ExportCell[][] = rows.map((row) => [
    { kind: 'text', value: row.decisionClass },
    // A null country is an estate-wide row (the platform's own docstring), not a missing one.
    row.countryKey === null || row.countryKey === undefined || row.countryKey === ''
      ? { kind: 'label', value: ESTATE_WIDE, code: ESTATE_WIDE }
      : { kind: 'text', value: row.countryKey },
    { kind: 'label', value: row.autonomyLevel, code: row.autonomyLevel },
    { kind: 'label', value: row.approverRole, code: row.approverRole },
    optionalNumber(row.maxValue),
    optionalNumber(row.maxVolume),
    optionalDate(row.reviewedOn),
    optionalDate(row.reviewDueOn),
  ]);
  return {
    id: 'foundations.autonomy.table',
    titleKey: 'foundations.autonomy.title',
    columns: [
      { key: 'decision_class', headerKey: 'foundations.autonomy.column.decisionClass', kind: 'text', descriptionKey: 'foundations.columnGuide.autonomy.decisionClass' },
      { key: 'country_key', headerKey: 'foundations.autonomy.column.country', kind: 'text', descriptionKey: 'foundations.columnGuide.autonomy.country' },
      { key: 'autonomy_level', headerKey: 'foundations.autonomy.column.autonomyLevel', kind: 'label', display: exportDisplayOf(AUTONOMY_LEVEL_LABEL_KEY), descriptionKey: 'foundations.columnGuide.autonomy.level' },
      { key: 'approver_role', headerKey: 'foundations.autonomy.column.approverRole', kind: 'label', display: exportDisplayOf(ROLE_LABEL_KEY), descriptionKey: 'foundations.columnGuide.autonomy.approverRole' },
      { key: 'max_value', headerKey: 'foundations.autonomy.column.maxValue', kind: 'number', descriptionKey: 'foundations.columnGuide.autonomy.maxValue' },
      { key: 'max_volume', headerKey: 'foundations.autonomy.column.maxVolume', kind: 'number', descriptionKey: 'foundations.columnGuide.autonomy.maxVolume' },
      { key: 'reviewed_on', headerKey: 'foundations.autonomy.column.reviewedOn', kind: 'date', descriptionKey: 'foundations.columnGuide.autonomy.reviewedOn' },
      { key: 'review_due_on', headerKey: 'foundations.autonomy.column.reviewDueOn', kind: 'date', descriptionKey: 'foundations.columnGuide.autonomy.reviewDueOn' },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: provenance([
      {
        measure: 'decision_class_autonomy',
        definition:
          'The autonomy granted to one decision class, estate-wide or in one country '
          + '(reqs/07 §3.5): the level, the role that approves above it, and its ceilings.',
      },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'decision_class', direction: 'asc' }],
      visibleColumns: [
        'decision_class', 'country_key', 'autonomy_level', 'approver_role',
        'max_value', 'max_volume', 'reviewed_on', 'review_due_on',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The Modules route's export (bata-ui#166 W1-7, ADR 2006): each module of the market the link
 * names (`?country=`), its state and the three settings as the registry resolves them — value,
 * source, since when and who approved. No market on the link: an empty sheet that says so in its
 * perimeter, never every market guessed.
 */
export async function modulesExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const country = readCountry(search);
  const response = country === null ? null : await callOperation(readModules, { query: { country_key: country } });
  const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'text', value });
  const rows: ExportCell[][] = (response?.modules ?? []).flatMap((module) => {
    const settings = [module.enabled, module.cadence, ...(module.excludedDoors === null ? [] : [module.excludedDoors])];
    return settings.map((setting): ExportCell[] => [
      { kind: 'text', value: module.module },
      { kind: 'label', value: module.state, code: module.state },
      { kind: 'text', value: setting.parameterKey },
      text(setting.value),
      { kind: 'label', value: setting.source, code: setting.source },
      text(setting.validFrom),
      // By name (usability audit B6), the ids beside them, technical.
      peopleExportCell(setting.approvedBy, setting.approvedByNames),
      text(setting.approvedBy.join(' ')),
      text(setting.approvedAt),
      text(setting.parameterSetId),
    ]);
  });
  return {
    id: 'foundations.modules.table',
    titleKey: 'foundations.modules.title',
    columns: [
      { key: 'module', headerKey: 'foundations.modules.column.module', kind: 'text', descriptionKey: 'foundations.columnGuide.module.module' },
      { key: 'state', headerKey: 'foundations.modules.column.state', kind: 'label', display: exportDisplay((code) => notFallback(moduleStateKey(code), 'foundations.modules.state.other')), descriptionKey: 'foundations.columnGuide.module.state' },
      { key: 'parameter_key', headerKey: 'foundations.column.parameterKey', kind: 'text', descriptionKey: 'foundations.columnGuide.module.parameterKey' },
      { key: 'value', headerKey: 'foundations.column.parameterValue', kind: 'text', descriptionKey: 'foundations.columnGuide.module.value' },
      { key: 'source', headerKey: 'foundations.modules.column.source', kind: 'label', display: exportDisplay((code) => notFallback(sourceKey(code), 'foundations.modules.source.other')), descriptionKey: 'foundations.columnGuide.module.source' },
      { key: 'valid_from', headerKey: 'foundations.modules.column.since', kind: 'text', descriptionKey: 'foundations.columnGuide.module.validFrom' },
      { key: 'approved_by_names', headerKey: 'foundations.modules.column.approvedBy', kind: 'text', descriptionKey: 'foundations.columnGuide.people.moduleApprovedByNames' },
      { key: 'approved_by', headerKey: 'foundations.people.column.approvedByIds', kind: 'text', technical: true, descriptionKey: 'foundations.columnGuide.people.approvedByIds' },
      { key: 'approved_at', headerKey: 'foundations.modules.column.approvedAt', kind: 'text', descriptionKey: 'foundations.columnGuide.module.approvedAt' },
      { key: 'change_id', headerKey: 'foundations.modules.column.changeId', kind: 'text', descriptionKey: 'foundations.columnGuide.module.changeId' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'module_setting', definition: 'The registry row in force for the market on the read’s date (country row, else estate row, else the platform’s stated default), with the approvals of the change set that authored it (bata-platform ADR 1004).' },
    ]),
    perimeter: {
      filters: country === null ? [] : [{ field: 'country_key', operator: '=', value: country }],
      sort: [{ key: 'module', direction: 'asc' }],
      visibleColumns: ['module', 'state', 'parameter_key', 'value', 'source', 'valid_from', 'approved_by_names', 'approved_by', 'approved_at', 'change_id'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * «Calendario dei processi» (ADR 9068): the year's periods as the page reads them — one row per
 * period, its type, days, name and mechanic, its lock and the days its runs are due — from the same
 * `GET /registry/process-calendar` the page reads, on today's date. No market or year, no rows.
 */
export async function processCalendarExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const country = readCountry(search);
  const year = readYear(search);
  const asOf = todayIso(new Date());
  const response = country === null || year === null
    ? null
    : await callOperation(readCalendar, { query: { country_key: country, year, as_of: asOf } });
  const text = (value: string | number | null | undefined): ExportCell => (value === null || value === undefined || value === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'text', value: String(value) });
  const rows: ExportCell[][] = (response?.periods ?? []).map((period) => [
    { kind: 'label', value: period.periodType, code: period.periodType },
    { kind: 'text', value: period.periodId },
    { kind: 'text', value: period.startsOn },
    { kind: 'text', value: period.endsOn },
    text(period.name),
    text(period.mechanic),
    { kind: 'label', value: period.locked ? (period.lockedBecause ?? 'started') : 'open', code: period.locked ? (period.lockedBecause ?? 'started') : 'open' },
    text(period.recommendationDueFrom),
    text(period.recommendationDueTo),
    text(period.executionDueFrom),
    text(period.executionDueTo),
    text(period.priceDueOn),
    text(period.version),
    text(period.changeId),
    text(period.recordedAt),
  ]);
  const lockKey: Readonly<Record<string, string>> = {
    open: 'foundations.calendar.lock.open', started: 'foundations.calendar.lock.started', run: 'foundations.calendar.lock.run',
  };
  return {
    id: 'foundations.processCalendar.table',
    titleKey: 'foundations.calendar.title',
    columns: [
      { key: 'period_type', headerKey: 'foundations.calendar.column.type', kind: 'label', display: exportDisplayOf(PERIOD_TYPE_KEY), descriptionKey: 'foundations.columnGuide.calendar.type' },
      { key: 'period_id', headerKey: 'foundations.calendar.column.period', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.period' },
      { key: 'starts_on', headerKey: 'foundations.calendar.column.startsOn', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.startsOn' },
      { key: 'ends_on', headerKey: 'foundations.calendar.column.endsOn', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.endsOn' },
      { key: 'name', headerKey: 'foundations.calendar.column.name', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.name' },
      { key: 'mechanic', headerKey: 'foundations.calendar.column.mechanic', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.mechanic' },
      { key: 'lock', headerKey: 'foundations.calendar.column.state', kind: 'label', display: exportDisplayOf(lockKey), descriptionKey: 'foundations.columnGuide.calendar.lock' },
      { key: 'recommendation_due_from', headerKey: 'foundations.calendar.column.recommendationDueFrom', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.recommendationDueFrom' },
      { key: 'recommendation_due_to', headerKey: 'foundations.calendar.column.recommendationDueTo', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.recommendationDueTo' },
      { key: 'execution_due_from', headerKey: 'foundations.calendar.column.executionDueFrom', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.executionDueFrom' },
      { key: 'execution_due_to', headerKey: 'foundations.calendar.column.executionDueTo', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.executionDueTo' },
      { key: 'price_due_on', headerKey: 'foundations.calendar.column.priceDueOn', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.priceDueOn' },
      { key: 'version', headerKey: 'foundations.calendar.column.version', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.version' },
      { key: 'change_id', headerKey: 'foundations.calendar.column.changeId', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.changeId' },
      { key: 'recorded_at', headerKey: 'foundations.calendar.column.recordedAt', kind: 'text', descriptionKey: 'foundations.columnGuide.calendar.recordedAt' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      { measure: 'process_calendar_period', definition: 'The approved version of each period of the market overlapping the year, as stood on the read’s date, with its lock (started, or a run made) and the days its runs are due (bata-platform ADR 2136).' },
    ]),
    perimeter: {
      filters: country === null || year === null ? [] : [
        { field: 'country_key', operator: '=', value: country },
        { field: 'year', operator: '=', value: String(year) },
        { field: 'as_of', operator: '=', value: asOf },
      ],
      sort: [{ key: 'starts_on', direction: 'asc' }],
      visibleColumns: [
        'period_type', 'period_id', 'starts_on', 'ends_on', 'name', 'mechanic', 'lock', 'recommendation_due_from',
        'recommendation_due_to', 'execution_due_from', 'execution_due_to', 'price_due_on', 'version', 'change_id', 'recorded_at',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
